import dayjs from "dayjs";
import { ArrowRight, CheckCircle2, Edit2, Info, Lock } from "lucide-react";
import { Button } from "@/v2/components/ui/button";
import type { BillingCycle, GiftStep } from "../types";

export function OrderSummaryRail({
  step,
  logoUrl,
  channelName,
  activeLabel,
  billing,
  termCount,
  formik,
  recipientHasSub,
  pricesReady,
  pricesError,
  planPrice,
  loadOptions,
  isLoadingData,
  onEditRecipient,
  onContinueStep1,
  onContinueStep2,
}: {
  step: GiftStep;
  logoUrl?: string;
  channelName: string;
  activeLabel: string;
  billing: BillingCycle;
  termCount: number;
  formik: any;
  recipientHasSub: boolean;
  pricesReady: boolean;
  pricesError: boolean;
  planPrice: number;
  loadOptions: () => void;
  isLoadingData: boolean;
  onEditRecipient: () => void;
  onContinueStep1: () => void;
  onContinueStep2: () => void;
}) {
  const termUnit = (upper: boolean) =>
    billing === "annual"
      ? termCount > 1
        ? upper
          ? "YEARS"
          : "Years"
        : upper
          ? "YEAR"
          : "Year"
      : termCount > 1
        ? upper
          ? "MONTHS"
          : "Months"
        : upper
          ? "MONTH"
          : "Month";

  return (
    <div className="xl:col-span-4 2xl:col-span-5 w-full min-w-0 xl:sticky xl:top-6">
      <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-xs">
        {/* Header Bar */}
        <div className="px-6 py-3.5 text-center font-extrabold text-base tracking-tight text-white bg-action rounded-t-2xl">
          {step === 1 ? "Gift Summary" : "Order Summary"}
        </div>

        <div className="p-6 space-y-5 bg-card rounded-b-2xl">
          {/* Inner Ticket Card - same bg/fg as the site header (--v2-nav-bg /
              --v2-nav-fg) rather than a bespoke dark card. */}
          <div className="rounded-xl p-5 text-center relative shadow-xs bg-(--v2-nav-bg) text-(--v2-nav-fg)">
            <div className="absolute top-3 right-3 text-xl">🎁</div>
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={channelName}
                className="h-9 object-contain mx-auto max-w-[140px]"
              />
            ) : (
              <div className="font-extrabold text-lg tracking-wider text-(--v2-nav-fg)">
                {channelName}
              </div>
            )}
            <p className="text-[11px] font-bold tracking-widest uppercase mt-3 text-(--v2-nav-fg) opacity-70">
              {`${activeLabel.toUpperCase()} PLAN`}
            </p>
            <p className="text-sm font-extrabold tracking-wider uppercase mt-0.5 text-(--v2-nav-fg)">
              {`${termCount} ${termUnit(true)}`}
            </p>
          </div>

          {/* Step 3 Recipient Details Summary Block */}
          {step === 3 && (formik.values.name || formik.values.email) && (
            <div className="rounded-xl border border-border p-3.5 bg-muted/50 space-y-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="font-bold text-muted-foreground uppercase tracking-wider text-[10px]">
                  Recipient
                </span>
                <button
                  type="button"
                  onClick={onEditRecipient}
                  className="font-bold hover:underline flex items-center gap-1 text-action"
                >
                  <Edit2 className="size-3" /> Edit
                </button>
              </div>
              <p className="font-bold text-sm text-foreground">
                {formik.values.name || "Recipient"}
              </p>
              <p className="text-muted-foreground truncate pb-2">
                {formik.values.email}
              </p>
              {formik.values.message && (
                <p className="text-muted-foreground italic line-clamp-2 pt-3 border-t border-border">
                  "{formik.values.message}"
                </p>
              )}
              <p className="text-muted-foreground pt-1">
                Delivery:{" "}
                <span className="font-semibold text-foreground">
                  {formik.values.send
                    ? "Send immediately"
                    : dayjs(formik.values.date).format("MMM D, YYYY")}
                </span>
              </p>
            </div>
          )}

          {/* Line Items */}
          <div className="space-y-2.5 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground font-medium">Plan</span>
              <span className="font-semibold text-right text-foreground">
                {activeLabel} ({termCount} {termUnit(false)})
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-muted-foreground font-medium">Length</span>
              <span className="font-semibold text-foreground">
                {termCount} {termUnit(false)}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-muted-foreground font-medium">
                Delivery
              </span>
              <span className="font-semibold text-foreground">
                {formik.values.send
                  ? "Send now"
                  : formik.values.date
                    ? dayjs(formik.values.date).format("MMM D, YYYY")
                    : "Scheduled"}
              </span>
            </div>

            {(step === 3 || step === 4) &&
              (formik.values.name || formik.values.email) && (
                <div className="flex justify-between items-start">
                  <span className="text-muted-foreground font-medium">
                    Recipient
                  </span>
                  <span className="text-right">
                    <span className="block font-semibold text-foreground">
                      {formik.values.name || "Recipient"}
                    </span>
                    <span className="block text-muted-foreground text-xs">
                      {formik.values.email}
                    </span>
                  </span>
                </div>
              )}

            <div className="flex justify-between items-center">
              <span className="text-muted-foreground font-medium">
                Recipient redeems
              </span>
              <span className="font-semibold text-foreground">
                {recipientHasSub ? "Account credit" : "One-tap link"}
              </span>
            </div>
          </div>

          <hr className="border-border" />

          {/* Financial Summary */}
          <div className="space-y-2 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground font-medium">
                Subtotal
              </span>
              <span className="font-semibold text-foreground flex items-center justify-end">
                {pricesReady && planPrice > 0 ? (
                  `$${planPrice.toFixed(2)}`
                ) : !pricesReady && !pricesError ? (
                  <div className="h-4 w-12 bg-muted animate-pulse rounded" />
                ) : (
                  "—"
                )}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-muted-foreground font-medium flex items-center gap-1">
                Estimated taxes <Info className="size-3 text-muted-foreground" />
              </span>
              <span className="font-semibold text-foreground">$0.00</span>
            </div>
          </div>

          <div className="flex justify-between items-baseline pt-2">
            <span className="text-2xl font-extrabold text-foreground">
              {step === 4 ? "Total paid" : "Total today"}
            </span>
            <span className="text-2xl font-black text-action flex items-center justify-end">
              {pricesReady && planPrice > 0 ? (
                `$${planPrice.toFixed(2)}`
              ) : !pricesReady && !pricesError ? (
                <div className="h-8 w-20 bg-muted animate-pulse rounded" />
              ) : (
                "—"
              )}
            </span>
          </div>

          {step === 4 && (
            <div className="rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm font-semibold flex items-center gap-2 px-4 py-3">
              <CheckCircle2 className="size-4" /> Payment successful
            </div>
          )}

          {pricesError && (
            <div className="text-destructive text-xs text-center pt-2">
              Couldn't load current pricing.{" "}
              <button
                type="button"
                onClick={loadOptions}
                className="underline font-bold"
              >
                Retry
              </button>
            </div>
          )}

          {/* CTA BUTTON */}
          {step === 1 && (
            <Button
              type="button"
              disabled={!pricesReady || pricesError || planPrice <= 0}
              onClick={onContinueStep1}
              className="hidden lg:flex w-full h-11 text-sm font-extrabold gap-1.5 bg-action text-white hover:bg-action-hover"
            >
              {!pricesReady && !pricesError ? "Loading prices..." : "Continue"}{" "}
              <ArrowRight className="size-4" />
            </Button>
          )}

          {step === 2 && (
            <Button
              type="button"
              disabled={isLoadingData}
              onClick={onContinueStep2}
              className="hidden lg:flex w-full h-11 text-sm font-extrabold gap-1.5 bg-action text-white hover:bg-action-hover"
            >
              {isLoadingData ? "Processing..." : "Continue to Payment"}{" "}
              <ArrowRight className="size-4" />
            </Button>
          )}

          {/* Step 3 has no CTA here: the Complete Purchase button lives inside
              the embedded Stripe payment form so it can trigger confirmation. */}

          {/* Helper Subtext */}
          <p className="text-[14px] text-muted-foreground text-center leading-relaxed mt-2">
            {step === 1 &&
              "You'll choose the recipient and delivery details on the next step."}
            {step === 2 &&
              "You'll review your order and payment method on the next step."}
            {step === 3 &&
              (recipientHasSub
                ? `You'll be charged today. Because your recipient already has an active subscription, your gift will be added to their ${channelName} account as credit and applied to their future invoices. Non-refundable, no cash value.`
                : `You'll be charged today. The gift is delivered on your chosen date, and applies to a new or existing ${channelName} account when your recipient redeems it. Non-refundable, no cash value.`)}
          </p>

          <div className="flex items-center justify-center gap-1.5 text-[12px] text-muted-foreground font-medium pt-1">
            <Lock className="size-3" />
            <span>Secure checkout powered by Stripe</span>
          </div>
        </div>
      </div>
    </div>
  );
}
